import React, { useCallback, useContext, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { createPortal } from 'react-dom'

import { useGetFormTemplateByIdQuery, useUpdateFormTemplateMutation } from '../../../store/ai/formTemplate'
import { useGetAllScorecardsQuery } from '../../../store/ai/scorecard'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { FormTemplateMappingFormValues } from '../../../models/FormTemplate'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'

import { FormTemplateMappingForm } from './FormTemplateMappingForm/FormTemplateMappingForm'
import { FormTemplateViewHeader } from './FormTemplateViewHeader'

import './FormTemplateView.scss'

export const FormTemplateUpdateView: React.FC = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()
  const { portalRef } = useContext(HeaderContext)

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  return (
    <>
      {portalRef?.current ? createPortal(<FormTemplateViewHeader />, portalRef.current) : null}
      {!documentSummaryEnabled ? <Upsell feature={DELTA_TRIAL_FEATURES['formTemplate']} /> : <FormTemplateUpdateViewContent />}
    </>
  )
}

const FormTemplateUpdateViewContent: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()
  const { formId = '', formTemplateId = '' } = useParams()

  const { data: formTemplate, isLoading: formTemplateLoading } = useGetFormTemplateByIdQuery(
    { accountId, formTemplateId },
    { refetchOnMountOrArgChange: true, skip: !formTemplateId },
  )

  const { data: scorecardsResponse, isLoading: scorecardsLoading } = useGetAllScorecardsQuery(
    {
      accountId: accountId,
      aiFeatureIds: formId,
    },
    { refetchOnMountOrArgChange: true, skip: !formId },
  )

  const [triggerUpdateFormTemplate, { isLoading: formTemplateUpdateLoading }] = useUpdateFormTemplateMutation()

  const scorecard = useMemo(() => scorecardsResponse?.data.find((scorecard) => scorecard.aiFeatureIds.includes(formId)) ?? null, [formId, scorecardsResponse])

  const handleSubmit = useCallback(
    async (formData: FormTemplateMappingFormValues) => {
      if (!formTemplate) {
        return
      }
      try {
        const { id: formTemplateId } = formTemplate
        const { data: updatedFormTemplate } = await triggerUpdateFormTemplate({ accountId, formTemplateId, payload: formData })
        if (!updatedFormTemplate) {
          return
        }

        navigate(`/workspace/setup/forms/form/details/${formId}/templates`)
      } catch (e) {
        console.error(e)
      }
    },
    [accountId, formId, formTemplate, navigate, triggerUpdateFormTemplate],
  )

  if (formTemplateLoading || scorecardsLoading) {
    return <Spinner />
  }

  if (!formTemplate || !scorecard) {
    return null
  }

  return (
    <FormTemplateMappingForm
      targets={formTemplate.targets}
      mapping={formTemplate.mapping}
      scorecardDescriptor={scorecard.descriptor}
      onSubmit={handleSubmit}
      submitLoading={formTemplateUpdateLoading}
    />
  )
}
